import { format, isWithinInterval, parseISO } from 'date-fns';
import { SupportedInsightsAsset } from 'mode-shared/constants/api';
import { isEnumValue } from 'mode-shared/utils/object';

export const formatPairToLabel = (pair: string) => {
  return pair?.replace('PERP_', '').replaceAll('_', '/') || '';
};

export const formatWalletAddress = (
  address: string | undefined | null,
  firstBreakpoint = 6,
  secondBreakpoint = 4
): string => {
  if (!address) {
    return '';
  }

  if (address.length > 25) {
    return `${address.slice(0, firstBreakpoint)}...${address.slice(address.length - secondBreakpoint, address.length)}`;
  }

  return address;
};

export const formatCurrency = (amount?: number | string | null, maximumFractionDigits?: number, symbol?: string) => {
  if (amount == null || amount === '') return '-';
  const formattedAmount = Number(amount).toLocaleString(undefined, { maximumFractionDigits });
  const decimals = formattedAmount.split('.')[1];
  return decimals == null && maximumFractionDigits === 2
    ? `${symbol ?? ''}${formattedAmount}.00`
    : `${symbol ?? ''}${formattedAmount}`;
};

export function abbreviateNumber(amount: number | string, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits,
  }).format(Number(amount));
}

export const formatNumber = (num: number, decimals = 2): string => {
  const standardFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return standardFormatter.format(num);
};

export const formatDate = (dateStr: string, formatStr: string = 'MM/dd/yyyy'): string => {
  try {
    if (!dateStr) return 'N/A';

    // Check if the string already has timezone information
    // ISO 8601 formats: 2026-01-23T16:32:55Z or 2026-01-23T16:32:55+00:00 or 2026-01-23 16:32:55+00
    const hasZ = dateStr.includes('Z');
    const hasTimezoneOffset = /[+-]\d{2}(:?\d{2})?$/.test(dateStr.trim());
    const hasTimezone = hasZ || hasTimezoneOffset;

    // Parse the date string
    // If it has timezone info, use as-is (Date constructor handles ISO 8601 with timezone)
    // Otherwise add 'Z' to treat as UTC
    let date: Date;
    if (hasTimezone) {
      date = new Date(dateStr);
    } else {
      // For strings without timezone, add 'Z' to treat as UTC
      date = new Date(dateStr + 'Z');
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateStr);
      return dateStr;
    }

    return format(date, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr;
  }
};

export const checkDateInRange = (date: string, startDate: string, endDate: string): boolean => {
  const targetDate = parseISO(date);
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  return isWithinInterval(targetDate, { start, end });
};

export const shortenString = (input: string, maxLength: number = 52): string => {
  if (input.length <= maxLength) {
    return input;
  }
  return input.substring(0, maxLength - 3) + '...';
};

export const scaleNumber = (number: number, decimal: number, fixedDecimals: number = 6): number => {
  if (isNaN(number)) {
    throw new Error('Invalid number string');
  }
  return parseFloat((number / Math.pow(10, decimal)).toFixed(fixedDecimals));
};

export const formatHexStringToScaledNumber = (hex: string, decimal: number): number => {
  const number = parseInt(hex, 16);
  return scaleNumber(number, decimal);
};

export const formatDateToHumanReadable = (date: Date, formatStr: string = 'MMMM do yyyy'): string => {
  // Ensure the date is treated as UTC to avoid timezone conversion
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return format(utcDate, formatStr);
};

export const formatFractionalNumberToPercentage = (number: number, maximumFractionDigits: number = 0): string => {
  const value = number * 100;
  const formatted = value.toFixed(maximumFractionDigits);
  return `${parseFloat(formatted)}%`;
};

export const formatSymbolLabel = (symbol: string) => symbol.replace(/_/g, '/').toUpperCase();
